/**
 * TPU Premium Hat Patches — upload preview + dimension auto-fill.
 *
 * Two concerns live here, merged into one file/one listener rather than two
 * separate inline <script> tags in theme.liquid:
 *
 * 1. Real images: Upload Kit renders a real <img class="uploadkit-thumbnail">
 *    with width/height ATTRIBUTES set to the file's TRUE pixel dimensions
 *    (the image data itself is a downscaled preview copy — attributes are
 *    the true source). We watch for it and populate the DPO size inputs at
 *    300 DPI. Upload Kit's FilePond widget doesn't fire native change
 *    events, so a MutationObserver on the thumbnail is the reliable signal.
 *
 * 2. PSD / PDF / EPS: confirmed via live DOM inspection that Upload Kit does
 *    NOT create .uploadkit-thumbnail at all for files it doesn't recognize
 *    as images — instead it renders:
 *      <div class="uploadkit-file-listing">
 *        <img class="uploadkit-fileicon" src=".../bin.svg">
 *        <span>original-filename.psd</span>
 *      </div>
 *    That generic-icon + filename pairing is the real, confirmed trigger.
 *    The actual file lives at the URL in a hidden input Upload Kit already
 *    uses to submit the cart line item property:
 *      .uploadkit-attribute-container input[name="properties[Uploaded Image]"]
 *    Its uu= param is the same Uploadcare UUID used in real thumbnail src
 *    values (confirmed by DOM comparison), so we fetch
 *    https://ucarecdn.com/{uuid}/ directly rather than Upload Kit's own
 *    download.html wrapper endpoint, whose behavior is unconfirmed.
 *
 *    PSD -> ag-psd flattens the composite to canvas -> PNG.
 *    PDF -> PDF.js renders page 1 to canvas -> PNG.
 *    EPS -> no browser render path exists (confirmed — server-side
 *      conversion only); Upload Kit's own icon+filename card already
 *      communicates "uploaded successfully", so it's left as the fallback.
 *      Dimensions still come from the file's %%BoundingBox header, so
 *      pricing is accurate even without a visual preview.
 *
 *    On success we CREATE a real <img class="uploadkit-thumbnail"> and
 *    insert it, which the dimension-populate logic above and the
 *    cap-preview observer (single-dtf-transfers-media-gallery.liquid) both
 *    already watch for — neither needs to know any of this exists.
 *
 * Conversion libraries (ag-psd, PDF.js) load lazily in the background from
 * page load, not gated on a PSD/PDF actually being detected, so they're
 * likely already cached by the time a file finishes uploading.
 */
(function () {
  var MAX_BYTES = 100 * 1024 * 1024;
  var LOG = '[tpu-file-preview]';

  // The Upload Kit mid-upload scrollTo misfire (see uploadkit-scroll-fix.js,
  // loaded site-wide) is suppressed there with a timeout-based fallback
  // window. This pipeline knows exactly when processing starts and ends, so
  // it calls that shared suppressor directly for a tighter, more precise
  // window instead of relying on the fallback timing.
  function suppressScroll(on) {
    if (window.uploadkitSuppressScroll) window.uploadkitSuppressScroll(on);
  }

  function populateDimensionsInches(widthInches, heightInches) {
    var wi = document.querySelector('.width input');
    var hi = document.querySelector('.height input');
    if (!wi || !hi) return;
    var MIN = 0.5;
    wi.value = wi.prevValue = Math.max(MIN, widthInches).toFixed(2);
    hi.value = hi.prevValue = Math.max(MIN, heightInches).toFixed(2);
    if (window.dpoOptions) dpoOptions.updatePrice();
  }
  function populateDimensions(pxWidth, pxHeight) {
    var DPI = 300;
    populateDimensionsInches(pxWidth / DPI, pxHeight / DPI);
  }

  // .uploadkit-field's input/textarea start hidden (CSS default in
  // tpu-file-preview.css) and reveal once a preview has settled — a real
  // image thumbnail, a converted PSD/PDF, or a determined no-preview state
  // (EPS/unsupported/failed). "Settled" either way, not just "succeeded" —
  // the customer shouldn't be blocked from leaving a note just because no
  // visual preview was possible.
  function revealNoteField() {
    document.querySelectorAll('.uploadkit-field').forEach(function (field) {
      field.classList.add('tpu-note-ready');
    });
  }

  // ---- Real image dimension auto-fill ----
  function initImageDimensionWatcher() {
    function measure(thumb) {
      var px = parseFloat(thumb.getAttribute('width'));
      var py = parseFloat(thumb.getAttribute('height'));
      if (!px || !py) return false;
      populateDimensions(px, py);
      revealNoteField();
      return true;
    }

    var observer = new MutationObserver(function () {
      var thumb = document.querySelector('.uploadkit-thumbnail');
      if (!thumb) return;
      // One measurement per upload; re-measure when the file is swapped.
      var sig = (thumb.getAttribute('src') || '') + '|' + thumb.getAttribute('width') + 'x' + thumb.getAttribute('height');
      if (thumb.dataset.tpuMeasured === sig) return;
      if (measure(thumb)) thumb.dataset.tpuMeasured = sig;
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'width', 'height'] });
  }

  // ---- PSD / PDF / EPS preview + dimensions ----
  function initFilePreview() {
    var libPromises = {};

    function loadScript(key, src) {
      if (window[key]) return Promise.resolve(window[key]);
      if (!libPromises[key]) {
        libPromises[key] = new Promise(function (resolve, reject) {
          var s = document.createElement('script');
          s.src = src;
          s.onload = function () { resolve(window[key]); };
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      return libPromises[key];
    }
    function loadAgPsd() {
      return loadScript('agPsd', 'https://cdn.jsdelivr.net/npm/ag-psd@25/dist/bundle.js');
    }
    function loadPdfJs() {
      return loadScript('pdfjsLib', 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js')
        .then(function (lib) {
          lib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
          return lib;
        });
    }
    // Fire both downloads in the background on page load rather than
    // waiting for a PSD/PDF to actually be detected — by the time Upload
    // Kit finishes processing the customer's file, the library is likely
    // already cached, instead of a cold ~1-2MB download sitting on the
    // critical path.
    loadAgPsd().catch(function () {});
    loadPdfJs().catch(function () {});

    // Preview-only encode, not the production file (the original PSD/PDF
    // stays what's actually attached to the order) — downscaling before
    // encoding measurably speeds up both the toBlob() call and the
    // resulting thumbnail's own load time. True dimensions for pricing
    // come from the header/PDF-page read, independent of this.
    var MAX_PREVIEW_DIMENSION = 1000;
    function toPngBlob(canvas) {
      var scale = Math.min(1, MAX_PREVIEW_DIMENSION / Math.max(canvas.width, canvas.height));
      if (scale < 1) {
        var small = document.createElement('canvas');
        small.width = Math.round(canvas.width * scale);
        small.height = Math.round(canvas.height * scale);
        small.getContext('2d').drawImage(canvas, 0, 0, small.width, small.height);
        canvas = small;
      }
      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (b) { b ? resolve(b) : reject(new Error('PNG encode failed')); }, 'image/png');
      });
    }

    // Backup verification only — filename extension is the primary signal
    // now that we control the fetch, but MIME/extension are known-flaky
    // for PSD/EPS per the file-preview spec, so this catches a mismatch.
    function sniffType(buffer) {
      var bytes = new Uint8Array(buffer.slice(0, 8));
      var ascii = String.fromCharCode.apply(null, bytes);
      if (ascii.indexOf('%PDF') === 0) return 'pdf';
      if (bytes[0] === 0x38 && bytes[1] === 0x42 && bytes[2] === 0x50 && bytes[3] === 0x53) return 'psd'; // "8BPS"
      if (ascii.indexOf('%!PS') === 0) return 'eps';
      if (bytes[0] === 0xC5 && bytes[1] === 0xD0 && bytes[2] === 0xD3 && bytes[3] === 0xC6) return 'eps'; // binary EPSF header
      return 'unsupported';
    }

    // PSD header: "8BPS" signature, then height at bytes 14-17, width at
    // 18-21, both big-endian uint32. Authoritative dimension source —
    // always present even when there's no flattened composite to preview
    // (file saved without "Maximize Compatibility"). Pricing accuracy must
    // never depend on whether a PNG preview can be built.
    function readPsdHeaderDimensions(buffer) {
      if (buffer.byteLength < 22) return null;
      var view = new DataView(buffer);
      return { height: view.getUint32(14), width: view.getUint32(18) };
    }

    // EPS has no preview render path (confirmed — server-side conversion
    // only), but it's a text-based format at the header level, so pixel
    // dimensions are still obtainable without any conversion: the
    // %%BoundingBox comment gives the artwork's bounds in points (1/72
    // inch — always 72, unrelated to the 300 DPI raster convention used
    // elsewhere).
    function readEpsBoundingBox(buffer) {
      var text = new TextDecoder('latin1').decode(buffer.slice(0, 8192));
      var match = text.match(/%%BoundingBox:\s*(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)/);
      if (!match) return null;
      var llx = parseFloat(match[1]), lly = parseFloat(match[2]);
      var urx = parseFloat(match[3]), ury = parseFloat(match[4]);
      return { widthPt: urx - llx, heightPt: ury - lly };
    }

    function convertPsd(buffer) {
      return loadAgPsd().then(function (agPsd) {
        var psd = agPsd.readPsd(buffer, { skipLayerImageData: true, skipThumbnail: true, useImageData: false });
        if (!psd.canvas) throw new Error('No composite image in PSD (needs "Maximize Compatibility")');
        return toPngBlob(psd.canvas).then(function (blob) { return { blob: blob, width: psd.width, height: psd.height }; });
      });
    }

    function convertPdf(buffer) {
      return loadPdfJs()
        .then(function (pdfjsLib) { return pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise; })
        .then(function (pdf) { return pdf.getPage(1); })
        .then(function (page) {
          // PDF page size is in points (1/72 inch) — always, regardless of
          // what scale the canvas below renders at. scale:2 there is a
          // resolution choice for the visual preview only; using its pixel
          // dimensions as if they were 300-DPI pixels (as the image path
          // does) would be wrong — 2x a 72pt page is 144 DPI, not 300.
          var basePoints = page.getViewport({ scale: 1 });
          var widthInches = basePoints.width / 72;
          var heightInches = basePoints.height / 72;

          var viewport = page.getViewport({ scale: 2 });
          var canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          return page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport }).promise
            .then(function () { return toPngBlob(canvas); })
            .then(function (blob) {
              return { blob: blob, width: canvas.width, height: canvas.height, widthInches: widthInches, heightInches: heightInches };
            });
        });
    }

    function insertThumbnail(afterEl, blob, width, height) {
      var img = document.createElement('img');
      img.className = 'uploadkit-thumbnail';
      img.width = width;
      img.height = height;
      img.setAttribute('width', width);
      img.setAttribute('height', height);
      img.dataset.tpuConverted = 'true';
      img.src = URL.createObjectURL(blob);
      afterEl.parentNode.insertBefore(img, afterEl.nextSibling);
      return img;
    }

    // Small status line inserted right after the generic icon+filename row,
    // used both as a "Processing…" indicator during conversion and as a
    // permanent note for types with no possible preview (EPS) — so both
    // states are visibly intentional instead of looking like silent failure.
    function setStatus(listing, message) {
      var status = listing.parentNode.querySelector('.tpu-preview-status');
      if (!status) {
        status = document.createElement('p');
        status.className = 'tpu-preview-status';
        listing.parentNode.insertBefore(status, listing.nextSibling);
      }
      status.textContent = message;
      return status;
    }
    function clearStatus(listing) {
      var status = listing.parentNode.querySelector('.tpu-preview-status');
      if (status) status.remove();
    }

    function handleFileListing(listing) {
      if (listing.dataset.tpuHandled) return;
      listing.dataset.tpuHandled = 'true';

      if (listing.querySelector('.uploadkit-thumbnail')) return; // real image, not our case

      var icon = listing.querySelector('.uploadkit-fileicon');
      if (!icon) return;

      var nameSpan = listing.querySelector('span');
      var filename = nameSpan ? nameSpan.textContent.trim() : '';
      var ext = (filename.split('.').pop() || '').toLowerCase();
      console.log(LOG, 'unrecognized file:', filename, '(ext:', ext + ')');

      var urlInput = document.querySelector('.uploadkit-attribute-container input[name="properties[Uploaded Image]"]');
      if (!urlInput || !urlInput.value) { console.log(LOG, 'no uploaded-file URL input found yet'); return; }

      // Give immediate feedback rather than leaving the generic icon sitting
      // there for the 3-4s the fetch+convert pipeline can take — this fires
      // before we even know the type yet, since the fetch itself is part of
      // that delay.
      listing.style.display = 'none';
      setStatus(listing, 'Processing your file…');

      // Upload Kit's own mid-upload scrollTo fires somewhere in this
      // window — suppressed until the pipeline settles, whichever branch
      // it settles through.
      suppressScroll(true);

      // Prefer the direct Uploadcare CDN URL over Upload Kit's own
      // download.html wrapper endpoint — confirmed via DOM inspection that
      // the hidden input's uu= UUID is the exact same UUID used in the real
      // thumbnail's ucarecdn.com src for normal images, so this reuses a
      // domain/path already proven to serve raw bytes reliably.
      var uuidMatch = urlInput.value.match(/[?&]uu=([0-9a-f-]{36})/i);
      var fileUrl = uuidMatch ? ('https://ucarecdn.com/' + uuidMatch[1] + '/') : urlInput.value;
      console.log(LOG, 'fetching:', fileUrl, uuidMatch ? '(via ucarecdn UUID)' : '(fallback to raw input value)');

      fetch(fileUrl, { mode: 'cors' })
        .then(function (r) {
          console.log(LOG, 'fetch status', r.status, 'content-type', r.headers.get('content-type'));
          return r.blob();
        })
        .then(function (blob) {
          console.log(LOG, 'blob size', blob.size, blob.type);
          if (blob.size > MAX_BYTES) throw new Error('File too large for browser preview');
          return blob.arrayBuffer();
        })
        .then(function (buffer) {
          var sniffed = sniffType(buffer);
          console.log(LOG, 'sniffed:', sniffed, '| filename ext:', ext);
          if (sniffed === 'unsupported' && new TextDecoder().decode(buffer.slice(0, 15)).indexOf('<') === 0) {
            throw new Error('URL returned an HTML page, not file bytes — endpoint may need different handling');
          }

          var type = (ext === 'psd' || sniffed === 'psd') ? 'psd'
            : (ext === 'pdf' || sniffed === 'pdf') ? 'pdf'
            : (ext === 'eps' || sniffed === 'eps') ? 'eps'
            : 'unsupported';

          if (type === 'psd') {
            setStatus(listing, 'Processing PSD preview…');
            var dims = readPsdHeaderDimensions(buffer);
            if (dims) {
              console.log(LOG, 'PSD dimensions:', dims.width + 'x' + dims.height + 'px');
              populateDimensions(dims.width, dims.height);
            }
            return convertPsd(buffer).then(function (r) {
              insertThumbnail(listing, r.blob, r.width, r.height);
              clearStatus(listing);
              revealNoteField();
            });
          }
          if (type === 'pdf') {
            setStatus(listing, 'Processing PDF preview…');
            return convertPdf(buffer).then(function (r) {
              console.log(LOG, 'PDF dimensions:', r.widthInches.toFixed(2) + '" x ' + r.heightInches.toFixed(2) + '"');
              populateDimensionsInches(r.widthInches, r.heightInches);
              insertThumbnail(listing, r.blob, r.width, r.height);
              clearStatus(listing);
              revealNoteField();
            });
          }
          if (type === 'eps') {
            var bbox = readEpsBoundingBox(buffer);
            if (bbox) {
              console.log(LOG, 'EPS BoundingBox:', bbox.widthPt.toFixed(1) + 'pt x ' + bbox.heightPt.toFixed(1) + 'pt');
              populateDimensionsInches(bbox.widthPt / 72, bbox.heightPt / 72);
            } else {
              console.log(LOG, 'EPS has no %%BoundingBox — dimensions unavailable, width/height left as entered');
            }
            // No browser render path exists for EPS (server-side conversion
            // only) — restore Upload Kit's own icon+filename and say so
            // plainly, rather than leaving it hidden with no explanation.
            listing.style.display = '';
            setStatus(listing, 'No visual preview available for EPS files — your file uploaded successfully and will be reviewed before production.');
            revealNoteField();
            return;
          }
          console.log(LOG, type + ' — no browser preview available, leaving Upload Kit\'s file card as-is');
          listing.style.display = '';
          clearStatus(listing);
          revealNoteField();
        })
        .catch(function (err) {
          console.error(LOG, 'FAILED:', err);
          // Fail open: restore Upload Kit's own card so the customer still
          // sees confirmation their file was received, even though our
          // preview attempt failed.
          listing.style.display = '';
          setStatus(listing, 'Your file uploaded successfully, but a preview could not be generated.');
          revealNoteField();
        })
        .finally(function () {
          suppressScroll(false);
        });
    }

    var observer = new MutationObserver(function () {
      document.querySelectorAll('.uploadkit-file-listing').forEach(handleFileListing);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    console.log(LOG, 'observer armed (file-listing based)');
  }

  document.addEventListener('DOMContentLoaded', function () {
    initImageDimensionWatcher();
    initFilePreview();
  });

  // Reused by tpu-change-quantity.js to pre-fill the same DPO size fields
  // this pipeline already knows how to populate correctly (0.5" minimum
  // clamp, dpoOptions.updatePrice() trigger) — one implementation, not two.
  window.tpuPopulateDimensionsInches = populateDimensionsInches;
})();
